import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';
import Cookies from 'js-cookie';
import { getApiUrl } from '@/config/api';
import { useToast } from '@/components/ui/use-toast';

const RestaurantMenuClaim = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [extractedItemsCount, setExtractedItemsCount] = useState<number>(0);
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [isClaiming, setIsClaiming] = useState(false);
  const [hasExtraction, setHasExtraction] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  useEffect(() => {
    // Check for pending extraction from frictionless flow
    const extractionCompleted = sessionStorage.getItem('extraction_completed') === 'true';
    const itemsExtracted = sessionStorage.getItem('preview_items_extracted');
    const sessionToken = sessionStorage.getItem('preview_session_token');
    const restName = sessionStorage.getItem('preview_restaurant_name');
    
    if (extractionCompleted && itemsExtracted && sessionToken) {
      setHasExtraction(true);
      setExtractedItemsCount(parseInt(itemsExtracted));
      setRestaurantName(restName || 'your restaurant');
      setIsChecking(false);
    } else {
      // No extraction found, redirect to menu upload
      setIsChecking(false);
      toast({
        title: "No extraction found",
        description: "Redirecting to menu upload...",
        variant: "destructive"
      });
      setTimeout(() => {
        navigate('/restaurant-menu-upload');
      }, 2000);
    }
  }, [navigate, toast]);
  
  const handleClaimExtraction = async () => {
    const sessionToken = sessionStorage.getItem('preview_session_token');
    if (!sessionToken) {
      toast({
        title: "Error",
        description: "No extraction session found. Please try extracting again.",
        variant: "destructive"
      });
      return;
    }
    
    setIsClaiming(true);
    
    try {
      const response = await fetch(getApiUrl('frictionless-onboarding/claim-extraction'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_token: sessionToken })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to claim extraction');
      }
      
      const result = await response.json();
      
      // Clear session storage after successful claim
      sessionStorage.removeItem('extraction_completed');
      sessionStorage.removeItem('preview_session_token');
      sessionStorage.removeItem('preview_items_extracted');
      sessionStorage.removeItem('preview_restaurant_name');
      sessionStorage.removeItem('pending_menu_extraction');
      sessionStorage.removeItem('extraction_source');
      sessionStorage.removeItem('extraction_job_id');
      
      toast({
        title: "Success!",
        description: `${result.items_count || extractedItemsCount} menu items have been added to your restaurant.`
      });
      
      // Update auth token with the new one that includes rest_id
      // The backend now returns a new token with the updated rest_id
      if (result.token) {
        Cookies.set('auth_token', result.token);
        console.log('Token updated with rest_id:', result.rest_id);
        
        // Also update the auth context if available
        if (result.user && window.authContext?.setUser) {
          window.authContext.setUser(result.user);
        }
      } else {
        // Fallback: Try to refresh token if not included in response
        console.log('No token in response, attempting refresh...');
        try {
          const refreshResponse = await fetch(getApiUrl('restaurants/refresh-token'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${Cookies.get('auth_token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            if (refreshData.token) {
              Cookies.set('auth_token', refreshData.token);
              console.log('Token refreshed with rest_id:', refreshData.user.rest_id);
            }
          }
        } catch (error) {
          console.log('Could not refresh token:', error);
        }
      }
      
      // Navigate to review page with the menu_id as URL parameter
      setTimeout(() => {
        navigate(`/restaurant-menu-preview?processing_id=${result.menu_id}&source=frictionless`);
      }, 1500);
      
    } catch (error) {
      console.error('Claim error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to claim extraction. Please try again.",
        variant: "destructive"
      });
      setIsClaiming(false);
    }
  };
  

  if (isChecking) {
    return (
      <div className="min-h-screen bg-dineai-blue relative overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/80">Checking for extracted menu...</p>
        </div>
      </div>
    );
  }

  if (!hasExtraction) {
    return (
      <div className="min-h-screen bg-dineai-blue relative overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">No extraction found</p>
          <p className="text-white/60">Redirecting to menu upload...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <motion.div 
          className="h-full bg-dineai-yellow"
          initial={{ width: '90%' }}
          animate={{ width: '100%' }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo className="text-white h-8" />
          </div>

          {/* Claim Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-xs flex items-center gap-1.5">
                  <CheckCircle className="w-3 h-3" />
                  EXTRACTION COMPLETE
                </span>
              </div>

              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5, delay: 0.2 }}
                className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle className="w-10 h-10 text-green-400" />
              </motion.div>

              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Your Menu is{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Ready!
                </span>
              </h1>
              
              <p className="text-white/80 text-base">
                We've successfully extracted your menu from {restaurantName}
              </p>
            </div>

            {/* Stats Card */}
            <motion.div 
              className="bg-white/5 rounded-xl p-6 mb-8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-center">
                <p className="text-5xl font-bold text-dineai-yellow mb-2">
                  {extractedItemsCount}
                </p>
                <p className="text-white/70 text-sm uppercase tracking-wider">
                  Menu Items Extracted
                </p>
              </div>
              
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-white/60 text-sm text-center">
                  All items are ready to be added to your restaurant menu
                </p>
              </div>
            </motion.div>

            <p className="text-white/70 text-sm text-center mb-8">
              Click below to add these items to your restaurant. You'll be able to review and edit them next.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleClaimExtraction}
                disabled={isClaiming}
                className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center justify-center gap-2"
              >
                {isClaiming ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Adding Menu Items...
                  </>
                ) : (
                  <>
                    Add Menu to My Restaurant
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Help text */}
          <p className="text-center text-white/40 text-xs mt-6">
            Your extracted menu data is secure and will be permanently saved to your restaurant
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RestaurantMenuClaim;